import { Activity, AlertTriangle, BarChart3, Bell, BellRing, Bot, Building2, Calendar, CalendarDays, ClipboardList, Clock, CreditCard, DollarSign, ExternalLink, Globe, HelpCircle, Home, KeyRound, Lock, LogOut, Menu, MessageSquare, Scissors, Search, Server, Settings, Shield, Star, Store, Timer, UserCheck, Users, X, XCircle, Zap, _, useEffect, useRef, useState } from "../core/vendor.js";
import { api } from "../core/api.js";
import { COLORS, MobileMenuContext, NavigateContext, ROLES, ROLE_PAGES, SpotlightContext, UserContext, getUserInitials, rolePagesFor, useIsMobile } from "../core/config.jsx";
import { APP_VERSION } from "../core/updates.js";
import { Wm } from "../core/ui.jsx"; // PRO_MARKE_R_v1


// SIDEBAR (role-aware)
// ============================================================
export const ALL_NAV_ITEMS = [
  // Admin items
  { id: "admin-dashboard", icon: Activity, label: "Admin Dashboard", section: "admin" },
  { id: "admin-salons", icon: Building2, label: "Salons verwalten", section: "admin" },
  { id: "admin-onboarding", icon: ClipboardList, label: "Onboarding-Fortschritt", section: "admin" },  /* ONBOARDING_VIEW_v1 */
  { id: "admin-monitor", icon: Shield, label: "System-Monitor", section: "admin" },
  { id: "admin-costs", icon: Server, label: "KI-Kosten & Metriken", section: "admin" },
    { id: "admin-pricing", icon: DollarSign, label: "Preise & Pläne" /* TAG_FIX_v1 */, section: "admin" },
  { id: "admin-audit", icon: Shield, label: "Audit-Log", section: "admin" },  /* AUDIT_LOG_UI_v1 */
  { id: "admin-frontend-errors", icon: AlertTriangle, label: "Frontend-Errors", section: "admin" },  /* FRONTEND_ERROR_LOG_v1 */
  { id: "admin-subscriptions", icon: CreditCard, label: "Abonnements", section: "admin" },
  { id: "admin-updates", icon: Zap, label: "Updates & Releases", section: "admin" },
  // Salon items
  { id: "dashboard", icon: Home, label: "Dashboard", section: "salon" },
  { id: "calendar", icon: CalendarDays, label: "Kalender", section: "salon" },
  { id: "bookings", icon: Calendar, label: "Termine", section: "salon" },
  { id: "customers", icon: Users, label: "Kunden (CRM)", section: "salon" },
  { id: "services", icon: Scissors, label: "Dienstleistungen", section: "salon" },
  /* NAV_PREISE_v1 entfernt 2026-05-08: redundant zu „Dienstleistungen" + Standalone-Page hatte kein Dark-Mode + Back-Button-Bug. Pricing pro Service verwaltet der Inhaber jetzt unter „Dienstleistungen". Das alte preise.html bleibt unter shearly.pro/preise für direkten Aufruf erhalten (Legacy). */
  { id: "employees", icon: UserCheck, label: "Mitarbeiter", section: "salon" },
  { id: "ai-calls", icon: Bot, label: "KI-Anrufe", section: "salon", featureFlag: "ai_calls" },
  { id: "messages", icon: MessageSquare, label: "SMS & Nachrichten", section: "salon", featureFlag: "sms_marketing" },
  { id: "online-booking", icon: Globe, label: "Online-Buchung", section: "salon", featureFlag: "online_booking" },
  { id: "shifts", icon: Clock, label: "Schichtplan", section: "salon" },
  /* TIME_TRACKING_v1 -- Zeiterfassung (Pflicht nach BAG 13.09.2022 + MiLoG für Friseur) */
  { id: "time-tracking", icon: Timer, label: "Zeiterfassung", section: "salon" },
  { id: "payments", icon: CreditCard, label: "Online-Vorkasse", section: "salon" }, /* POS_TILLHUB_v1 -- umbenannt */
  { id: "pos", icon: Store, label: "Kassensystem", section: "salon" },              /* POS_TILLHUB_v1 -- neu */
  { id: "kasse", icon: Store, label: "Kasse", section: "salon", featureFlag: "pos_shearly" }, /* POS_SHEARLY_SSO_v1 -- Shearly Kasse, nur wenn Admin das Modul freigeschaltet hat */
  { id: "vouchers", icon: Star, label: "Gutscheine", section: "salon", featureFlag: "vouchers" },
  { id: "accounting", icon: BarChart3, label: "Buchhaltung", section: "salon", featureFlag: "datev_export" },
  /* TAX_CONSULTANT_v1 -- nur fuer Steuerberater-Rolle sichtbar (via ROLE_PAGES gesteuert) */
  { id: "tax-dashboard", icon: BarChart3, label: "Steuerberater-Bereich", section: "salon" },
  { id: "google", icon: ExternalLink, label: "Google Business", section: "salon", featureFlag: "google_business" },
  // Shared
  { id: "help", icon: HelpCircle, label: "Hilfe", section: "shared" },
  { id: "dsgvo", icon: Shield, label: "DSGVO", section: "shared" },
  { id: "profile", icon: Settings, label: "Mein Profil", section: "general" },
  { id: "subscription", icon: CreditCard, label: "Mein Abo", section: "salon" },
      { id: "settings", icon: Settings, label: "Einstellungen", section: "shared" },
  // Legal pages
  { id: "datenschutz", icon: Lock, label: "Datenschutz", section: "legal" },
  { id: "agb", icon: Globe, label: "AGB", section: "legal" },
  { id: "avv", icon: Shield, label: "AVV", section: "legal" },
  { id: "impressum", icon: Globe, label: "Impressum", section: "legal" },
];

export const Sidebar = ({ active, setActive, collapsed, setCollapsed, currentUser, onLogout, mobileOpen, setMobileOpen, enabledFeatures = {} }) => {
  const isMobile = useIsMobile();
  const isManager = currentUser.role === "employee" && currentUser.is_manager;
  const hasCustomPages = currentUser.role === "employee" && Array.isArray(currentUser.manager_pages) && currentUser.manager_pages.length > 0 && currentUser.manager_pages.length < 30 && currentUser.manager_pages.every(p => typeof p === "string" && p.length < 50);
  const allowedPages = hasCustomPages
    ? [...(currentUser.manager_pages || []), "profile", "help"]
    : rolePagesFor(currentUser);  // SALON_LOGIN_PRICES_v1
  const items = ALL_NAV_ITEMS.filter(item => {
    if (!allowedPages.includes(item.id)) return false;
    // Check feature flag if item has one (admin always sees everything)
    if (item.featureFlag && currentUser.role !== "admin" && Object.keys(enabledFeatures).length > 0) {
      return enabledFeatures[item.featureFlag] !== false;
    }
    return true;
  });
  const roleInfo = isManager ? { ...ROLES[currentUser.role], label: "Salonleitung", description: "Salon-Manager" } : ROLES[currentUser.role];

  // COLOR_THEME_v1 -- Sidebar-Gradient nutzt jetzt Theme-Farben (vorher hardcoded Lila)
  const gradients = {
    admin: "linear-gradient(180deg, #991B1B 0%, #DC2626 100%)",
    owner: `linear-gradient(180deg, ${COLORS.primaryDark} 0%, ${COLORS.primary} 100%)`,
    employee: "linear-gradient(180deg, #1E40AF 0%, #3B82F6 100%)",
    // SEC_BLOCK2_v1 -- fehlten: Seitenleiste der Kanzlei/Rezeption war im hellen Modus weiß auf weiß (Farben wie ROLES)
    tax_consultant: "linear-gradient(180deg, #047857 0%, #10B981 100%)",
    receptionist: "linear-gradient(180deg, #0369A1 0%, #0EA5E9 100%)",
  };

  const showExpanded = isMobile ? true : !collapsed;
  const sidebarWidth = isMobile ? 260 : (collapsed ? 64 : 240);
  if (isMobile && !mobileOpen) return null;

  const handleNavClick = (id) => { setActive(id); if (isMobile) setMobileOpen(false); };

  const sidebar = (
    // SIDEBAR_DVH_v1 -- className fuer 100dvh-Override (iPad-Leiste), s. CSS im head
    <div className="shearly-sidebar" style={{
      width: sidebarWidth, height: "100vh", background: gradients[currentUser.role],
      color: "white", display: "flex", flexDirection: "column", transition: "width 0.2s", flexShrink: 0, overflow: "hidden",
      ...(isMobile ? { position: "fixed", top: 0, left: 0, zIndex: 2000, boxShadow: "4px 0 20px rgba(0,0,0,0.3)" } : {})
    }}>
      <div style={{ padding: isMobile ? "calc(20px + env(safe-area-inset-top, 0px)) 16px 20px" /* SAFE_AREA_TOP_v1 */ : showExpanded ? "20px 16px" : "16px 8px", display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
        {/* PRO_WORTMARKE_v1 (Toby 29.10.2026) -- reiner Schriftzug wie shearly.de statt Schere + „Shearly PRO“ (BRAND_TAG_v1) */}
        {showExpanded && <span className="sp-wordmark sp-wordmark-paper" style={{ fontSize: 22 }}>Shearly<sup>®</sup></span>}
        <button onClick={() => isMobile ? setMobileOpen(false) : setCollapsed(!collapsed)} style={{
          marginLeft: "auto", background: "rgba(255,255,255,0.15)", border: "none", color: "white",
          borderRadius: 6, width: 28, height: 28, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          {isMobile ? <X size={16} /> : <Menu size={16} />}
        </button>
      </div>

      {showExpanded && (
        <div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{currentUser.name}</div>
          <div style={{ fontSize: 11, opacity: 0.7, display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
            <KeyRound size={10} /> {roleInfo.label}
          </div>
        </div>
      )}

      {/* SIDEBAR_DVH_v1 -- minHeight:0 erlaubt dem Menue zu schrumpfen (Flexbox-Default
          min-height:auto verhindert das) -> "Abmelden" wird nie mehr nach unten rausgedrueckt */}
      <nav style={{ flex: 1, minHeight: 0, padding: "8px 0", overflowY: "auto" }}>
        {items.map((item, idx) => {
          const Icon = item.icon;
          const isActive = active === item.id;
          const prevSection = idx > 0 ? items[idx - 1].section : null;
          const showDivider = prevSection && prevSection !== item.section;
          return (
            <div key={item.id}>
              {showDivider && <div style={{ height: 1, background: "rgba(255,255,255,0.12)", margin: "6px 16px" }} />}
              <button onClick={() => handleNavClick(item.id)} style={{
                display: "flex", alignItems: "center", gap: 12, width: "100%", padding: showExpanded ? "10px 20px" : "12px 0",
                justifyContent: showExpanded ? "flex-start" : "center",
                background: isActive ? "rgba(255,255,255,0.2)" : "transparent", border: "none", color: "white",
                cursor: "pointer", fontSize: 14, borderRadius: 0, borderLeft: isActive ? "3px solid white" : "3px solid transparent",
                opacity: isActive ? 1 : 0.75, fontWeight: isActive ? 600 : 400, transition: "all 0.15s"
              }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.1)" }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent" }}
              >
                <Icon size={18} />
                {showExpanded && <span>{item.label}</span>}
              </button>
            </div>
          );
        })}
      </nav>

      <button onClick={onLogout} style={{
        display: "flex", alignItems: "center", gap: 12, width: "100%", padding: showExpanded ? "12px 16px" : "12px 0",
        justifyContent: showExpanded ? "flex-start" : "center",
        background: "rgba(255,255,255,0.08)", border: "none", borderTop: "1px solid rgba(255,255,255,0.15)",
        color: "white", cursor: "pointer", fontSize: 13, opacity: 0.8
      }}>
        <LogOut size={16} />
        {showExpanded && <span>Abmelden</span>}
      </button>

      {showExpanded && (
        <div style={{ padding: "8px 16px", fontSize: 11, opacity: 0.5 }}>
          {/* APP_VERSION_v1 -- war statisch "v1.0", jetzt an PRODUCT_UPDATES gekoppelt */}
          <Wm /> {typeof APP_VERSION !== "undefined" ? APP_VERSION : "V1.4"} &middot; {roleInfo.label}
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1999 }} onClick={() => setMobileOpen(false)}>
        <div onClick={e => e.stopPropagation()}>{sidebar}</div>
      </div>
    );
  }
  return sidebar;
};

// --- Notification Bell ---
export const NOTIF_ICONS = {
  new_booking: { icon: Calendar, color: COLORS.success, label: "Neue Buchung" },
  cancellation: { icon: XCircle, color: COLORS.danger, label: "Stornierung" },
  urgent_cancellation: { icon: AlertTriangle, color: "#DC2626", label: "KURZFRISTIGE ABSAGE" },
  new_review: { icon: Star, color: COLORS.warning, label: "Bewertung" },
  system: { icon: Settings, color: COLORS.info, label: "System" },
  subscription: { icon: CreditCard, color: COLORS.primary, label: "Abo" },
  new_salon: { icon: Store, color: COLORS.success, label: "Neuer Salon" },
  critical_error: { icon: AlertTriangle, color: COLORS.danger, label: "Fehler" },
  widerruf: { icon: AlertTriangle, color: "#DC2626", label: "WIDERRUF (gesetzlich)" }, /* WIDERRUF_356A_v1 */
};

export const NotificationBell = () => {
  const onNavigate = React.useContext(NavigateContext);
  const isMobile = useIsMobile();  // NOTIF_PANEL_MOBILE_v1
  const [open, setOpen] = React.useState(false);
  const [notifications, setNotifications] = React.useState([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [urgentCount, setUrgentCount] = React.useState(0);
  const bellRef = React.useRef(null);

  // ONLINE_BOOKING_TOAST_v1 -- Banner-Toast oben fuer 30 Sek bei neuer Online-Buchung.
  // Filter strikt auf type === "new_booking" (wird NUR vom PublicBookingHandler erzeugt).
  // Manuell angelegte Termine erzeugen keine "new_booking"-Notification — also kein Toast.
  const [toasts, setToasts] = React.useState([]); // [{id, title, message, link, addedAt}]
  const seenIdsRef = React.useRef(new Set());
  const firstFetchRef = React.useRef(true);
  const removeToast = React.useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const failCountRef = React.useRef(0);
  const fetchNotifications = React.useCallback(() => {
    if (failCountRef.current >= 3) return;
    api.request("/notifications?limit=20")
      .then(res => {
        if (res.ok && res.data) {
          const list = res.data.notifications || [];
          setNotifications(list);
          setUnreadCount(res.data.unread_count || 0);
          setUrgentCount(res.data.urgent_count || 0);

          // ONLINE_BOOKING_TOAST_v1 -- neue new_booking-Notifications -> Toast
          // Beim allerersten Fetch nur "seen" markieren, kein Toast (keine alten Buchungen aufpoppen)
          const newToasts = [];
          list.forEach(n => {
            if (seenIdsRef.current.has(n.id)) return;
            seenIdsRef.current.add(n.id);
            if (firstFetchRef.current) return; // initial: silent seed
            if (n.type !== "new_booking") return;
            if (n.is_read) return;
            // nur frische Buchungen toasten (max 5 Min alt) — schuetzt vor 10-Min-Polling-Lag
            try {
              const ageSec = (Date.now() - new Date(n.created_at).getTime()) / 1000;
              if (ageSec > 300) return;
            } catch (e) {}
            newToasts.push({
              id: n.id, title: n.title, message: n.message,
              link: n.link, addedAt: Date.now()
            });
          });
          if (newToasts.length > 0) {
            setToasts(prev => [...prev, ...newToasts]);
            newToasts.forEach(t => {
              setTimeout(() => removeToast(t.id), 30000);
            });
          }
          firstFetchRef.current = false;
        }
      }).catch(() => { failCountRef.current += 1; });
  }, [removeToast]);

  React.useEffect(() => {
    fetchNotifications();
    // ONLINE_BOOKING_TOAST_v1 -- Poll alle 20 Sek (vorher 60s) damit Toasts zeitnah erscheinen
    const interval = setInterval(fetchNotifications, 20000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleToastClick = (toast) => {
    // Notification als gelesen markieren + navigate
    api.request("/notifications", {
      method: "PUT",
      body: JSON.stringify({ ids: [toast.id] })
    }).then(() => fetchNotifications()).catch(() => {});
    // NOTIF_DEEP_LINK_v1 -- Toast ist immer fuer new_booking -> appointments refresh
    try { window.dispatchEvent(new CustomEvent("shearly-refresh-appts")); } catch (e) {}
    if (toast.link && onNavigate) onNavigate(toast.link);
    removeToast(toast.id);
  };

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markAllRead = () => {
    api.request("/notifications", {
      method: "PUT",
      body: JSON.stringify({ all: true })
    }).then(() => { fetchNotifications(); });
  };

  const handleClick = (n) => {
    if (!n.is_read) {
      api.request("/notifications", {
        method: "PUT",
        body: JSON.stringify({ ids: [n.id] })
      }).then(() => fetchNotifications());
    }
    // NOTIF_DEEP_LINK_v1 -- Wenn neue Online-Buchung: appointments sofort re-fetchen,
    // damit der Termin im Kalender vorhanden ist bevor Pulse-Highlight greift.
    if (n.type === "new_booking") {
      try { window.dispatchEvent(new CustomEvent("shearly-refresh-appts")); } catch (e) {}
    }
    if (n.link && onNavigate) onNavigate(n.link);
    setOpen(false);
  };

  const timeAgo = (dateStr) => {
    const now = new Date();
    const d = new Date(dateStr);
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return "gerade eben";
    if (diff < 3600) return Math.floor(diff / 60) + " Min.";
    if (diff < 86400) return Math.floor(diff / 3600) + " Std.";
    return Math.floor(diff / 86400) + " Tage";
  };

  const hasUrgent = urgentCount > 0;

  return (
    <div ref={bellRef} style={{ position: "relative" }}>
      {/* ONLINE_BOOKING_TOAST_v1 -- Toast-Stack oben mittig, max ~30 Sek je Toast */}
      {toasts.length > 0 && (
        <div style={{
          position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)",
          zIndex: 10000, display: "flex", flexDirection: "column", gap: 8,
          pointerEvents: "none", maxWidth: "calc(100vw - 32px)"
        }}>
          {toasts.map(t => (
            <div key={t.id}
                 onClick={() => handleToastClick(t)}
                 style={{
                   pointerEvents: "auto", cursor: "pointer",
                   background: "linear-gradient(135deg, #059669 0%, #10B981 100%)",
                   color: "#fff", padding: "12px 18px", borderRadius: 12,
                   minWidth: 280, maxWidth: 440,
                   boxShadow: "0 12px 28px rgba(0,0,0,0.25), 0 4px 10px rgba(16,185,129,0.35)",
                   display: "flex", alignItems: "center", gap: 12,
                   animation: "shearlyToastIn 0.35s ease-out"
                 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,0.22)",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                fontSize: 18
              }}>📅</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.25 }}>{t.title}</div>
                <div style={{ fontSize: 12, opacity: 0.95, marginTop: 2,
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {t.message}
                </div>
                <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4, fontWeight: 600 }}>
                  Klicken, um direkt zum Termin zu springen →
                </div>
              </div>
              <button onClick={(e) => { e.stopPropagation(); removeToast(t.id); }}
                      style={{
                        background: "rgba(0,0,0,0.18)", border: "none", color: "#fff",
                        width: 24, height: 24, borderRadius: 12, cursor: "pointer",
                        fontSize: 14, fontWeight: 700, lineHeight: 1, padding: 0, flexShrink: 0
                      }}
                      title="Schließen">×</button>
            </div>
          ))}
        </div>
      )}
      {/* Urgent pulse ring */}
      {hasUrgent && !open && (
        <div style={{
          position: "absolute", top: -4, right: -4, width: 32, height: 32, borderRadius: "50%",
          border: "2px solid #DC2626", animation: "urgentPulse 1.5s ease-in-out infinite", pointerEvents: "none", zIndex: 1
        }} />
      )}
      <button onClick={() => setOpen(!open)} style={{
        background: "none", border: "none", cursor: "pointer", padding: 4, position: "relative"
      }}>
        {hasUrgent ? <BellRing size={20} color="#DC2626" style={{ animation: "bellShake 0.5s ease-in-out infinite" }} /> :
          <Bell size={20} color={open ? COLORS.primary : COLORS.textLight} />}
        {unreadCount > 0 && (
          <div style={{
            position: "absolute", top: -2, right: -4, minWidth: 16, height: 16, borderRadius: 8,
            background: hasUrgent ? "#DC2626" : COLORS.danger, color: "white", fontSize: 10, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px",
            animation: hasUrgent ? "urgentPulse 1.5s ease-in-out infinite" : "none"
          }}>{unreadCount > 9 ? "9+" : unreadCount}</div>
        )}
      </button>

      {open && (
        <div style={{
          // NOTIF_PANEL_MOBILE_v1 (17.09.2026, Christianes Handy-Screenshot: Fenster ragte links aus dem Bild) --
          // auf Handybreite nicht mehr an der Glocke aufhaengen (360 px breit, rechtsbuendig), sondern fest
          // unter der Kopfzeile ueber die volle Breite mit 8 px Rand; Desktop unveraendert.
          ...(isMobile
            ? { position: "fixed", top: ((bellRef.current && bellRef.current.getBoundingClientRect().bottom) || 56) + 8, left: 8, right: 8, width: "auto", maxHeight: "calc(100vh - 90px)" }
            : { position: "absolute", top: "calc(100% + 8px)", right: 0, width: 360, maxHeight: 440 }),
          background: COLORS.card, borderRadius: 12, boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
          border: `1px solid ${COLORS.border}`, zIndex: 1000, overflow: "hidden",
          display: "flex", flexDirection: "column"
        }}>
          {/* Header */}
          <div style={{
            padding: "14px 16px", borderBottom: `1px solid ${COLORS.border}`,
            display: "flex", justifyContent: "space-between", alignItems: "center"
          }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: COLORS.text }}>Benachrichtigungen</span>
            {unreadCount > 0 && (
              <button onClick={markAllRead} style={{
                background: "none", border: "none", color: COLORS.primary,
                fontSize: 12, cursor: "pointer", fontWeight: 600
              }}>Alle gelesen</button>
            )}
          </div>

          {/* List */}
          <div style={{ overflowY: "auto", maxHeight: 380 }}>
            {notifications.length === 0 ? (
              <div style={{ padding: 32, textAlign: "center", color: COLORS.textLight, fontSize: 14 }}>
                Keine Benachrichtigungen
              </div>
            ) : notifications.map(n => {
              const meta = NOTIF_ICONS[n.type] || NOTIF_ICONS.system;
              const Icon = meta.icon;
              const isUrgent = n.urgent && !n.is_read;
              const bgColor = isUrgent ? "#FEF2F2" : (n.is_read ? "transparent" : (COLORS.primary + "08"));
              return (
                <div key={n.id} onClick={() => handleClick(n)} style={{
                  padding: "12px 16px", display: "flex", gap: 12, cursor: "pointer",
                  background: bgColor,
                  borderBottom: `1px solid ${isUrgent ? "#FECACA" : COLORS.border}`,
                  borderLeft: isUrgent ? "3px solid #DC2626" : "3px solid transparent",
                  transition: "background 0.15s"
                }}
                onMouseOver={e => e.currentTarget.style.background = COLORS.bg}
                onMouseOut={e => e.currentTarget.style.background = bgColor}
                >
                  <div style={{
                    width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                    background: isUrgent ? "#FEE2E2" : (meta.color + "15"),
                    display: "flex", alignItems: "center", justifyContent: "center",
                    animation: isUrgent ? "urgentPulse 2s ease-in-out infinite" : "none"
                  }}>
                    <Icon size={16} color={meta.color} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <span style={{ fontWeight: n.is_read ? 500 : 700, fontSize: 13, color: isUrgent ? "#DC2626" : COLORS.text }}>{n.title}</span>
                      {!n.is_read && <div style={{ width: 7, height: 7, borderRadius: "50%", background: isUrgent ? "#DC2626" : COLORS.primary, flexShrink: 0 }} />}
                    </div>
                    <p style={{ margin: "2px 0 0", fontSize: 12, color: COLORS.textLight, lineHeight: 1.4,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.message}</p>
                    <span style={{ fontSize: 11, color: COLORS.textLight, opacity: 0.7 }}>{timeAgo(n.created_at)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// --- Header ---
export const Header = ({ title, subtitle, compact }) => {  // SEASONAL_DECOR_v1 (h): compact = engerer Kopf (nur Kalender)
  const isMobile = useIsMobile();
  const openMenu = React.useContext(MobileMenuContext);
  // HEADER_AVATAR_v1 -- Initialen aus currentUser.name (vorher hardcoded "AD")
  const user = React.useContext(UserContext);
  const initials = getUserInitials(user && user.name);
  const navigate = React.useContext(NavigateContext);
  // SPOTLIGHT_v1.2 -- Lupen-Button für Touch-Devices (iPad ohne externe Tastatur)
  // funktioniert aber natürlich überall. Desktop bekommt zusätzlich den Shortcut-Hint.
  const openSpotlight = React.useContext(SpotlightContext);
  const isMac = typeof navigator !== "undefined" && /(Mac|iPhone|iPad|iPod)/i.test(navigator.platform || "");
  const shortcutLabel = isMac ? "⌘K" : "Strg+K";
  // AVATAR_MENU_v1 (6.1) -- Dropdown-Menue mit Dark-Mode-Toggle + Profil/Logout
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState(typeof window !== "undefined" ? (localStorage.getItem("theme") || "dark") : "dark");
  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    try { localStorage.setItem("theme", next); } catch(e) {}
    document.documentElement.setAttribute("data-theme", next);
    setTheme(next);
  };
  React.useEffect(() => {
    const close = (e) => { if (menuOpen && !e.target.closest("[data-avatar-menu]")) setMenuOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: compact ? (isMobile ? 10 : 14) : (isMobile ? 16 : 24), gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        {isMobile && openMenu && (
          <button onClick={openMenu} aria-label="Menü öffnen" /* SAFE_AREA_TOP_v1: Tippflaeche 44x44 (Apple-Minimum), Optik unveraendert durch negativen Rand */ style={{ background: "none", border: "none", cursor: "pointer", padding: 11, margin: -7, flexShrink: 0 }}>
            <Menu size={22} color={COLORS.text} />
          </button>
        )}
        <div style={{ minWidth: 0 }}>
          {/* POLISH_v1: kraeftigere Header (groesser, bolder, tighter letter-spacing) */}
          <h1 style={{ fontSize: isMobile ? 22 : 30, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.1, color: COLORS.text, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</h1>
          {subtitle && <p style={{ color: COLORS.textLight, margin: compact ? "2px 0 0" : "6px 0 0", fontSize: isMobile ? 12 : 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</p>}
        </div>
      </div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexShrink: 0 }}>
        {/* SPOTLIGHT_v1.2 -- Lupen-Button öffnet Cmd+K-Spotlight per Touch/Click */}
        {openSpotlight && (
          <button
            onClick={openSpotlight}
            title={"Schnell-Suche (" + shortcutLabel + ")"}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "transparent", border: "1px solid " + COLORS.border,
              borderRadius: 8, padding: isMobile ? "6px 8px" : "6px 10px",
              cursor: "pointer", color: COLORS.textLight,
              fontSize: 12, fontWeight: 500,
            }}>
            <Search size={16} />
            {!isMobile && (
              <span style={{ fontSize: 11, fontFamily: "ui-monospace, monospace", opacity: 0.85 }}>{shortcutLabel}</span>
            )}
          </button>
        )}
        {/* SEC_BLOCK2_v1 (SEC-002) -- Salon-Hinweise (Kundennamen) nicht für die Steuerberatung, Server antwortet 403 */}
        {(user && user.role === "tax_consultant") ? null : <NotificationBell />}
        {/* AVATAR_MENU_v1 (6.1) -- Dropdown mit Dark-Mode-Toggle, Profil, Logout */}
        {/* AVATAR_MENU_MOBILE_v1 (M1): vorher !isMobile -- Hell/Dunkel + Farbthema waren am Handy unerreichbar */}
        {(
          <div data-avatar-menu style={{ position: "relative" }}>
            <button onClick={() => setMenuOpen(o => !o)}
              title={user && user.name ? user.name : ""}
              style={{
                width: isMobile ? 32 : 36, height: isMobile ? 32 : 36, borderRadius: "50%", background: COLORS.primary, color: "white",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: isMobile ? 13 : 14, fontWeight: 600,
                border: "none", cursor: "pointer", padding: 0, flexShrink: 0,
              }}>{initials}</button>
            {menuOpen && (
              <div style={{
                position: "absolute", top: "calc(100% + 8px)", right: 0, minWidth: 200,
                background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 12,
                boxShadow: "0 8px 24px rgba(0,0,0,0.15)", zIndex: 1000, overflow: "hidden",
              }}>
                <div style={{ padding: "10px 14px", borderBottom: `1px solid ${COLORS.border}` }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{user && user.name}</div>
                  <div style={{ fontSize: 11, color: COLORS.textLight }}>{user && user.email}</div>
                </div>
                <button onClick={() => { setMenuOpen(false); navigate && navigate("profile"); }}
                  style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", border: "none", background: "transparent", cursor: "pointer", fontSize: 13, color: COLORS.text, textAlign: "left" }}>
                  <Settings size={14} /> Profil & Einstellungen
                </button>
                <button onClick={toggleTheme}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "10px 14px", border: "none", background: "transparent", cursor: "pointer", fontSize: 13, color: COLORS.text, textAlign: "left" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    {theme === "dark" ? "🌙" : "☀️"} {theme === "dark" ? "Dunkel" : "Hell"}
                  </span>
                  <span style={{ fontSize: 11, color: COLORS.textLight }}>tippen um zu wechseln</span>
                </button>
                {/* COLOR_THEME_v2 -- Farbthema-Auswahl: Lila / Gold / Schwarz / Grau */}
                <div style={{ padding: "10px 14px", borderTop: `1px solid ${COLORS.border}` }}>
                  <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 8 }}>Farbthema</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {[
                      { key: "purple",  label: "Lila",    color: "#7C3AED" },
                      { key: "gold",    label: "Gold",    color: "#D4AF6E" },
                      { key: "schwarz", label: "Schwarz", color: "#1F2937" },
                      { key: "grau",    label: "Grau",    color: "#6B7280" },
                    ].map(t => {
                      const cur = (() => { try { const v = localStorage.getItem("shearly_color_theme"); return ["purple","gold","schwarz","grau"].includes(v) ? v : "purple"; } catch { return "purple"; } })();
                      const isActive = cur === t.key;
                      return (
                        <button key={t.key} title={t.label}
                          onClick={() => { try { localStorage.setItem("shearly_color_theme", t.key); } catch(e) {} window.location.reload(); }}
                          style={{
                            width: 30, height: 30, borderRadius: "50%", background: t.color,
                            border: isActive ? `3px solid ${COLORS.text}` : `1px solid ${COLORS.border}`,
                            cursor: "pointer", padding: 0, flexShrink: 0,
                          }} />
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const WhatsNewModal = ({ updates, onDismiss }) => {
  if (!updates || updates.length === 0) return null;
  return (
    <div onClick={onDismiss} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 99998,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "var(--card)", borderRadius: 16, width: "min(560px, 95vw)",
        maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
        border: "1px solid var(--border)",
      }}>
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textLight, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Neu in <Wm /></div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: COLORS.text }}>
            Was ist neu für dich
          </h2>
        </div>
        <div style={{ overflowY: "auto", padding: "12px 24px", flex: 1 }}>
          {updates.map((u, idx) => (
            <div key={u.id} style={{
              padding: "16px 0",
              borderBottom: idx < updates.length - 1 ? "1px dashed var(--border)" : "none"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6, gap: 8 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: COLORS.text }}>{u.title}</h3>
                <span style={{ fontSize: 11, color: COLORS.textLight, flexShrink: 0 }}>{u.date}</span>
              </div>
              <div style={{ fontSize: 13.5, lineHeight: 1.55, color: COLORS.textLight }}>{u.body}</div>
            </div>
          ))}
        </div>
        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 11, color: COLORS.textLight }}>
            Diese Übersicht zeigen wir nur einmal pro Update.
          </span>
          <button onClick={onDismiss} style={{
            padding: "10px 20px", borderRadius: 10, border: "none",
            background: COLORS.primary || "#7C3AED", color: "white",
            fontSize: 14, fontWeight: 700, cursor: "pointer",
          }}>Cool, verstanden</button>
        </div>
      </div>
    </div>
  );
};

// FIRST_LOGIN_GREETING_v1 -- Begrüßungsfenster für den ERSTEN Anmelder des Tages pro Salon
// (Toby 14.07.2026, Dashboard-Umbau Teil 2). Ob es erscheint, entscheidet der SERVER
// (POST /api/greeting/first-login, atomare Tages-Markierung salons.greeting_shown_date,
// erst ab 6:00 Uhr) -- localStorage reicht nicht, das gilt nur pro Gerät.
// Layout bewusst Schwarz-Weiß (Toby-Wunsch). Keine Eingabefelder, daher keine Remount-Falle.
export const FirstLoginGreeting = ({ userName, todayCount, onDismiss }) => {
  const hour = new Date().getHours();
  const firstName = String(userName || "").trim().split(" ")[0] || "du";
  const greet = hour < 11 ? "Guten Morgen" : hour < 18 ? "Hallo" : "Guten Abend";
  const weekday = new Date().toLocaleDateString("de-DE", { weekday: "long" });
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#ffffff", color: "#111111", borderRadius: 16, width: "min(460px, 95vw)", padding: "36px 32px", textAlign: "center", border: "2px solid #111111", boxShadow: "0 24px 64px rgba(0,0,0,0.5)" }}>
        <div style={{ fontSize: 44, marginBottom: 12 }}>✂️</div>
        <h2 style={{ margin: "0 0 10px", fontSize: 24, fontWeight: 800, color: "#111111" }}>{greet}, {firstName}!</h2>
        <p style={{ margin: "0 0 6px", fontSize: 14.5, lineHeight: 1.6, color: "#333333" }}>
          Du bist heute die erste Anmeldung im Salon — ein frischer {weekday} liegt vor dir.
        </p>
        <p style={{ margin: "0 0 24px", fontSize: 14.5, lineHeight: 1.6, color: "#333333" }}>
          {todayCount > 0
            ? <React.Fragment>Heute {todayCount === 1 ? "steht" : "stehen"} <strong>{todayCount} Termin{todayCount === 1 ? "" : "e"}</strong> im Kalender. Viel Freude dabei — deine Kundschaft ist bei dir in den besten Händen!</React.Fragment>
            : <React.Fragment>Der Kalender ist heute noch frei — vielleicht genau der richtige Moment für alles, was sonst liegen bleibt. Du machst das großartig!</React.Fragment>}
        </p>
        <button onClick={onDismiss} style={{ padding: "12px 24px", borderRadius: 10, border: "2px solid #111111", background: "#111111", color: "#ffffff", fontSize: 15, fontWeight: 800, cursor: "pointer" }}>
          Schere gewetzt — los geht’s!
        </button>
      </div>
    </div>
  );
};

export const ConfettiOverlay = ({ milestone, onClose }) => {
  if (!milestone) return null;
  const pieces = React.useMemo(() => Array.from({length: 60}, (_, i) => ({
    id: i,
    color: ["#7C3AED","#10B981","#F59E0B","#EF4444","#3B82F6","#EC4899","#FBBF24"][i % 7],
    left: Math.random() * 100,
    delay: Math.random() * 0.6,
    rotate: Math.random() * 360,
    size: 8 + Math.random() * 8,
  })), [milestone]);
  const title = milestone === 1 ? "Erste Online-Buchung!" : milestone + ". Online-Buchung!";
  const msg = milestone === 1 ? "Glückwunsch zum allerersten Online-Kunden 🚀"
            : milestone <= 50 ? "Klasse — dein Online-Buchen läuft an!"
            : milestone <= 100 ? "Cool — 100 Buchungen geknackt! 🏆"
            : "Beeindruckend — du bist im Online-Buchen voll dabei! ⭐";
  return (
    <div>
      <style>{`
        @keyframes shearlyConfettiFall {
          0% { transform: translateY(-20vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(120vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 100000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.35)" }}>
        {pieces.map(p => (
          <div key={p.id} style={{
            position: "absolute", left: p.left + "%", top: 0,
            width: p.size, height: p.size * 1.4,
            background: p.color, borderRadius: 2,
            animation: "shearlyConfettiFall 3.2s ease-out " + p.delay + "s forwards",
            transform: "rotate(" + p.rotate + "deg)",
            pointerEvents: "none",
          }} />
        ))}
        <div onClick={e => e.stopPropagation()} style={{
          background: "var(--card)", padding: "32px 40px", borderRadius: 20,
          textAlign: "center", boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
          border: "2px solid " + (COLORS.primary || "#7C3AED"), maxWidth: 420,
          position: "relative", zIndex: 1,
        }}>
          <div style={{ fontSize: 60, marginBottom: 10 }}>🎉</div>
          <h2 style={{ margin: "0 0 8px", fontSize: 26, fontWeight: 800, color: COLORS.text }}>
            {title}
          </h2>
          <p style={{ fontSize: 14, color: COLORS.textLight, margin: "0 0 20px", lineHeight: 1.5 }}>
            {msg}
          </p>
          <button onClick={onClose} style={{
            padding: "10px 24px", borderRadius: 10, border: "none",
            background: COLORS.primary || "#7C3AED", color: "white",
            fontSize: 14, fontWeight: 700, cursor: "pointer",
          }}>Cool, danke!</button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// SPOTLIGHT_v1 -- Cmd+K Global-Suche
// Use-Cases:
//   - Kunde ruft an und fragt "Wann hatte ich meinen Termin?" → "sarah" tippen → Enter
//   - Schnell zu Mitarbeiter / Service / Termin springen ohne Sidebar-Navigation
//   - Datum-Suche: "21.05" findet alle Termine an dem Tag
// Trigger: Cmd+K (Mac) oder Ctrl+K (Win/Linux). Esc schließt. ↑↓ navigieren. Enter öffnet.
// SPOTLIGHT_v1.1: useEffect für Listener MUSS vor early returns von App() liegen
//                 (React Hooks Rule of Order — sonst Blackscreen nach Login).
// ============================================================
export const SpotlightSearch = ({ open, onClose, customers, appointments, employees, services, onOpenCustomer }) => {
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const navigate = React.useContext(NavigateContext);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setCursor(0);
      setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
    }
  }, [open]);

  const results = React.useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return [];
    const out = [];

    // SPOTLIGHT_CRM_JUMP_v1 -- Ranking statt blindem Substring-Cut: „oli" fand vorher
    // 7× „Carolin" (enthält o-l-i) und schnitt bei 6 ab — „Oli Müller" erschien NIE.
    // Score: 0 = Name beginnt mit Suchwort, 1 = ein Wort beginnt damit, 2 = nur enthalten.
    const cust = (customers || []).map(c => {
      const name = (c.name || "").toLowerCase();
      const phone = (c.phone || "").toString().toLowerCase();
      let s = -1;
      if (name.startsWith(q)) s = 0;
      else if (name.split(/\s+/).some(w => w.startsWith(q))) s = 1;
      else if (name.includes(q) || phone.includes(q)) s = 2;
      return s < 0 ? null : { c, s };
    }).filter(Boolean).sort((a, b) => a.s - b.s).slice(0, 6).map(({ c }) => ({
      type: "customer",
      id: c.id,
      label: c.name || "Kunde",
      secondary: [
        c.customerType === "stammkunde" ? "Stamm" : c.customerType === "laufkunde" ? "Laufkunde" : "",
        c.phone,
        c.lastVisit ? `letzter Besuch ${c.lastVisit}` : ""
      ].filter(Boolean).join(" · "),
      icon: "👤",
      // SPOTLIGHT_CRM_JUMP_v1 -- direkt ins Kundenprofil springen statt nur zur Seite
      go: () => { if (onOpenCustomer) onOpenCustomer(c.id); navigate("customers"); },
    }));
    out.push(...cust);

    const appts = (appointments || []).filter(a => {
      const c = (customers || []).find(cc => cc.id === a.customerId);
      const e = (employees || []).find(ee => ee.id === a.employeeId);
      const custName = (c && c.name ? c.name : "").toLowerCase();
      const empName = (e && e.name ? e.name : "").toLowerCase();
      const dateStr = (a.date || "").toString();
      const dateGerman = dateStr.split("-").reverse().join(".");
      return custName.includes(q) || empName.includes(q) || dateStr.includes(q) || dateGerman.includes(q);
    }).slice(0, 6).map(a => {
      const c = (customers || []).find(cc => cc.id === a.customerId);
      const e = (employees || []).find(ee => ee.id === a.employeeId);
      const svcs = a.serviceNames || ((a.serviceIds || []).map(sid => {
        const s = (services || []).find(ss => ss.id === sid);
        return s ? s.name : null;
      }).filter(Boolean).join(", "));
      const dateGerman = (a.date || "").split("-").reverse().join(".");
      return {
        type: "appointment",
        id: a.id,
        label: ((c && c.name) || "Kunde") + " — " + dateGerman + (a.startTime ? " um " + a.startTime : ""),
        secondary: [svcs, (e && e.name)].filter(Boolean).join(" · "),
        icon: "📅",
        go: () => navigate("calendar:" + a.date + ":" + a.id),
      };
    });
    out.push(...appts);

    const emps = (employees || []).filter(e => (e.name || "").toLowerCase().includes(q)).slice(0, 4).map(e => ({
      type: "employee",
      id: e.id,
      label: e.name,
      secondary: e.role || (e.is_active === 0 ? "inaktiv" : "aktiv"),
      icon: "💇",
      go: () => navigate("employees"),
    }));
    out.push(...emps);

    const svcs = (services || []).filter(s => {
      const name = (s.name || "").toLowerCase();
      const isActive = s.is_active === undefined || s.is_active === 1 || s.is_active === true;
      return name.includes(q) && isActive;
    }).slice(0, 4).map(s => ({
      type: "service",
      id: s.id,
      label: s.name,
      secondary: (s.duration || s.base_duration || 30) + " Min · ab " + (s.price || s.base_price || 0) + " €",
      icon: "✂️",
      go: () => navigate("services"),
    }));
    out.push(...svcs);

    return out;
  // SPOTLIGHT_SYNC_FIX_v1 -- zusätzlich Inhalts-Hash der appointments (id+start+date+emp) in deps.
  // Falls Parent-State das Array referenz-gleich behält (z.B. wegen guardedSetAppointments im Demo-Modus
  // oder map mit identischer Mutation), erkennt useMemo via Hash-String trotzdem den Inhaltswechsel.
  }, [query, customers, appointments, employees, services, navigate, onOpenCustomer,
      (appointments || []).map(a => a.id + ":" + (a.startTime || "") + ":" + (a.date || "") + ":" + (a.employeeId || "")).join("|")
     ]);

  useEffect(() => { if (cursor >= results.length) setCursor(0); }, [results.length, cursor]);

  const handleKey = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setCursor(c => Math.min(c + 1, Math.max(0, results.length - 1))); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const sel = results[cursor];
      if (sel && sel.go) { sel.go(); onClose(); }
    }
  };

  if (!open) return null;

  const typeLabel = t => t === "customer" ? "Kunde" : t === "appointment" ? "Termin" : t === "employee" ? "MA" : t === "service" ? "Service" : t;

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
      zIndex: 99999, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "12vh",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "var(--card)", borderRadius: 14, width: "min(640px, 92vw)",
        maxHeight: "72vh", overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
        border: "1px solid var(--border)",
      }}>
        <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>🔍</span>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Kunden, Termine, Mitarbeiter, Services suchen..."
            autoComplete="off" spellCheck="false"
            style={{
              flex: 1, border: "none", outline: "none", background: "transparent",
              fontSize: 16, color: "var(--text)", fontFamily: "inherit",
            }}
          />
          <kbd style={{ fontSize: 11, padding: "3px 7px", background: "var(--cardHover)", borderRadius: 4, color: "var(--textLight)", fontFamily: "monospace" }}>esc</kbd>
        </div>
        <div style={{ overflowY: "auto", flex: 1 }}>
          {query.trim() === "" ? (
            <div style={{ padding: 24, textAlign: "center", color: "var(--textLight)", fontSize: 13, lineHeight: 1.9 }}>
              Tippe um zu suchen.<br/>
              <span style={{ fontSize: 11.5, opacity: 0.85 }}>z.B. „sarah" (Kunde) · „21.05" (Termine) · „mariella" (MA) · „schnitt" (Service)</span>
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "var(--textLight)", fontSize: 13 }}>
              Keine Treffer für „{query}"
            </div>
          ) : (
            results.map((r, idx) => (
              <div
                key={r.type + "-" + r.id}
                onClick={() => { r.go(); onClose(); }}
                onMouseEnter={() => setCursor(idx)}
                style={{
                  padding: "11px 16px",
                  cursor: "pointer",
                  background: cursor === idx ? "var(--cardHover)" : "transparent",
                  borderLeft: cursor === idx ? "3px solid " + (COLORS.primary || "#7C3AED") : "3px solid transparent",
                  display: "flex", alignItems: "center", gap: 12,
                }}
              >
                <span style={{ fontSize: 18, opacity: 0.85, minWidth: 22 }}>{r.icon}</span>
                <div style={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.label}</div>
                  {r.secondary && (
                    <div style={{ fontSize: 11.5, color: "var(--textLight)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 2 }}>{r.secondary}</div>
                  )}
                </div>
                <span style={{ fontSize: 10, color: "var(--textLight)", textTransform: "uppercase", letterSpacing: 0.6, padding: "2px 8px", background: "var(--cardHover)", borderRadius: 4, flexShrink: 0 }}>
                  {typeLabel(r.type)}
                </span>
              </div>
            ))
          )}
        </div>
        <div style={{ padding: "8px 16px", borderTop: "1px solid var(--border)", fontSize: 11, color: "var(--textLight)", display: "flex", gap: 14, justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <kbd style={{ padding: "2px 6px", background: "var(--cardHover)", borderRadius: 3, fontFamily: "monospace" }}>↑↓</kbd>
            <span style={{ margin: "0 6px" }}>navigieren</span>
            <kbd style={{ padding: "2px 6px", background: "var(--cardHover)", borderRadius: 3, fontFamily: "monospace" }}>↵</kbd>
            <span style={{ marginLeft: 6 }}>öffnen</span>
          </div>
          <div style={{ opacity: 0.7 }}>Cmd+K / Ctrl+K</div>
        </div>
      </div>
    </div>
  );
};
